// src/charts/AnalyticsGrid.jsx
import React from "react";
import { Col, Row, Card } from "antd";
import CountUp from "react-countup";

import UniversalChart from "./UniversalChart";
import {
  getDaysDifference,
  getRandomColor,
  isNumber,
  safeParse,
} from "../../helpers/helper";
import { getIcon } from "../atoms/FuzzyIcon";
import SectionHeader from "../atoms/SectionHeader";

/* -------------------------
  configs & helpers
--------------------------*/
const MATH_ACTIONS = new Set(["sum", "count"]);
const DATA_ACTIONS = new Set(["parse", "flat"]);
const configs = {
  events: {
    "Total Products": [["eventAgreements", "total"]],
    "Total Cost": [
      ["eventAgreements", "data", "action:flat", "action:sum:amount"],
      ["eventInfo", "extras", "action:parse", "expenses", "action:sum:1"],
    ],
    "Total Other Expense": [["eventInfo", "extras", "action:parse", "expenses", "action:sum:1"]],
    "CountDown to Event": [["eventInfo", "startDate"]],
  },
};

const executeDataAction = (action, dataCopy) => {
  if (!dataCopy) return;
  if (action == "parse") return safeParse(dataCopy);
  if (action == "flat") return Object.values(dataCopy).flat();
  return dataCopy;
};

const executeMathAction = (action, key, dataCopy) => {
  if (!dataCopy) return 0;
  let sum = 0;
  if (action == "sum") {
    if (isNumber(key)) {
      for (const d of dataCopy) sum += parseInt(d[parseInt(key)]);
    } else {
      for (const d of dataCopy) sum += (d && d[key] ? parseInt(d[key]):0);
    }
  }
  return sum;
};

const AnalyticsDataExtractor = (data, configs) => {
  let totalSum = 0;

  for (const config of configs) {
    let dataCopy = { ...data };
    const actions = [...config];
    let i = 0;

    for (let action of actions) {
      if (!dataCopy) return totalSum;
      if (String(action).startsWith("action")) {
        action = action.split(":");
        if (DATA_ACTIONS.has(action[1])) dataCopy = executeDataAction(action[1], dataCopy);
        if (MATH_ACTIONS.has(action[1])) {
          const value = executeMathAction(action[1], action[2], dataCopy);
          if (action[1] == "sum" && isNumber(value)) totalSum += value;
        }
      } else {
        dataCopy = dataCopy[action];
        if (actions.length - 1 == i) return dataCopy;
      }
      i += 1;
    }
  }

  return totalSum;
};

/* -------------------------
  UI helpers
--------------------------*/

// Build canonical sparkline data for MetricCard / UniversalChart:
// canonical format: [{ x, y, ... }]
function buildSparkData(value) {
  // normalize single-value arrays and plain numbers
  if (Array.isArray(value) && value.length === 0) return [];
  if (Array.isArray(value) && value.length === 1) value = value[0];

  if (value == null || value === "-" || (typeof value === "string" && value.trim() === "")) return [];

  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    // small trend that ends at numeric (tweaked for pleasant spike)
    const points = [
      0,
      Math.max(0, Math.round(numeric * 0.5)), // mid-low
      Math.max(0, Math.round(numeric * 0.35)), // dip
      Math.max(0, Math.round(numeric * 0.6)), // bounce
      numeric, // final
    ];
    return points.map((p, i) => ({ x: `t${i}`, y: p }));
  }

  // otherwise return empty (no spark)
  return [];
}


/* -------------------------
  AnalyticsGrid component
--------------------------*/
const AnalyticsGrid = ({ config = "events", data = {} }) => {
  const cfg = configs[config] || {};
  const keys = Object.keys(cfg);
  const colSpan = keys.length ? Math.floor(24 / keys.length) : 8;

  return (
    <Card key="analyticsgrid" style={{ marginTop: 10 }}>

      <Row gutter={16} >
        {keys.map((item) => {
          // Total* items (render as metric with spark)
          if (item.startsWith("Total")) {
            const extracted = AnalyticsDataExtractor(data, cfg[item]);
            const numeric = isNumber(extracted) ? Number(extracted) : extracted;
            const sparkData = buildSparkData(numeric);

            return (
              <Col
                key={item}
                style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                span={colSpan}
              >
                <UniversalChart
                  type="metric"
                  title={item}
                  subtitle={item + ' for this suite/event'}
                  value={numeric}

                  data={sparkData}

                  colors={[getRandomColor()]}
                />
              </Col>
            );
          }

          // CountDown* items (render as metric without spark)
          if (item.startsWith("CountDown")) {
            const rawDate = AnalyticsDataExtractor(data, cfg[item]);
            const days = rawDate ? getDaysDifference(new Date(rawDate)) : null;
            const displayValue = days != null ? (days > 0 ? `${days} Days` : "0 Days") : "-";

            return (
              <Col
                key={item}
                style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                span={colSpan}
              >
                <UniversalChart
                  type="metric"
                  title={item}
                  subtitle={"Countdown to Event/Suite Start Date"}
                  value={displayValue}
                  data={[]} // no spark
                  height={92}
                  width={"100%"}
                  style={{width:"100%"}}

                />
              </Col>
            );
          }

          // fallback: render as empty placeholder
          return (
            <Col
              key={item}
              style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
              span={colSpan}
            >
              <div style={{ padding: 12, color: "#6b7280" }}>{item}</div>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
};

export default AnalyticsGrid;
